import { formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Employee } from 'src/app/models/employee.model';
import { EmployeeAddress } from 'src/app/models/employee-address.model';
import { EmployeeContactInfoRequest } from 'src/app/models/requests/employee.contact-info-request.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { EmployeeAddressService } from 'src/app/services/repositories/employee-address.service';
import { EmployeeContactInfoService } from 'src/app/services/repositories/employee-contact-info.service';
import { EmployeeService } from 'src/app/services/repositories/employees.service';
import { LoginService } from 'src/app/services/repositories/login.service';
import { EyeValidators } from 'src/app/validators/eyeValidators';
import { PaymentService } from 'src/app/services/repositories/payment.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { EMAIL_REGEX, PASSWORD_MIN_LENGTH, PASSWORD_REGEX } from 'src/app/helpers/regex.helper';
import { ToastrService } from 'ngx-toastr';
import { AddressFormComponent } from 'src/app/ui-widgets/forms/address-form/address-form.component';
import { finalize } from 'rxjs';


@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: false
})
export class ProfileComponent implements OnInit {
  @ViewChild('addressForm') addressComponent!: AddressFormComponent

  toggleClass: boolean = true;
  employee: Employee | undefined;
  contactSubmitted = false;
  loginSubmitted = false;
  censoredCardData: string;
  passwordMinLength = PASSWORD_MIN_LENGTH;

  private readonly N_SUCCESS_PASSWORD_TITLE = 'Password Update';
  private readonly N_SUCCESS_PASSWORD_MSG = 'Your password has been updated.';

  private readonly N_SUCCESS_ADDRESS_TITLE = 'Home Address';
  private readonly N_SUCCESS_ADDRESS_MSG = 'Your address has been updated.';

  private readonly N_CONTACT_TITLE = 'Contact Info';
  private readonly N_CONTACT_SUCCESS_MSG = 'Your contact info has been updated.';

  private readonly N_GENERAL_INFO_TITLE = 'General Info';
  private readonly N_GENERAL_INFO_SUCCESS_MSG = 'Your general info has been updated.';

  private readonly N_UPDATE_ERROR_MSG = 'Unable to update your info. Please contact Eyelation Customer Support.';

  get lfc() { return this.loginForm.controls; }
  get cfc() { return this.contactInfoForm.controls; }
  get gfc() { return this.generalForm.controls; }

  isShowOptIn: boolean = false;

  loginForm: UntypedFormGroup = new UntypedFormGroup({
    username: new UntypedFormControl({ value: '', disabled: true }),
    newPassword: new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(PASSWORD_MIN_LENGTH),
      Validators.pattern(PASSWORD_REGEX),
      EyeValidators.NoWhiteSpace
    ]),
    confirmPassword: new UntypedFormControl('', [Validators.required])
  },
    {
      validators: EyeValidators.MustMatch('newPassword', 'confirmPassword')
    });

  contactInfoForm: UntypedFormGroup = new UntypedFormGroup({
    emailAddress: new UntypedFormControl('', [Validators.pattern(EMAIL_REGEX)]),
    homePhone: new UntypedFormControl('', [Validators.pattern("^[0-9]*$"),
    Validators.minLength(10), Validators.maxLength(10)]), // masking applied for phone numbers
    mobilePhone: new UntypedFormControl('', [Validators.pattern("^[0-9]*$"),
    Validators.minLength(10),
    Validators.maxLength(10)]),
    isOptedIn: new UntypedFormControl('', [])
  },
    {
      validators: EyeValidators.RequiredOneOf(['emailAddress', 'homePhone', 'mobilePhone'])
    });

  generalForm: UntypedFormGroup = new UntypedFormGroup({
    dateOfBirth: new UntypedFormControl('', []),
    gender: new UntypedFormControl('', []),
  });

  constructor(private _authService: AuthenticationService,
    private _employeeService: EmployeeService,
    private _employeeAddressService: EmployeeAddressService,
    private _employeeContactInfoService: EmployeeContactInfoService,
    private _loginService: LoginService,
    private _paymentService: PaymentService,
    private _loaderService: NgxUiLoaderService,
    private _notificationService: ToastrService) {
    this.censoredCardData = '';
  }

  ngOnInit(): void {
    this.loadEmployeeInfo();
    this.loadDefaultPaymentMethod();
  }

  onKeyUp(event: any) {
    const phonePattern = /[ ]|[_-]|\+|\(|\)/g;
    if (this.employee?.contactInfo.isOptedIn) {
      let currentNumber = event.target.value
      currentNumber = currentNumber.replace(phonePattern, '');
      const oldNumber = this.employee.contactInfo.mobilePhone;
      if (currentNumber == oldNumber && this.isShowOptIn) {
        this.isShowOptIn = false;
        this.cfc.isOptedIn.patchValue(true);
      } else {
        this.isShowOptIn = true;
        this.cfc.isOptedIn.patchValue(false);
      }
    }
  }

  loadEmployeeInfo() {
    this._employeeService.getEmployeeInfo()
      .subscribe({
        next: (data: Employee) => {
          this.employee = data;
          this.loadLoginForm();
          this.loadContactInfoForm();
          this.loadGeneralForm();
        },
        error: (error: any) =>  console.log(error)
      });
  }

  loadDefaultPaymentMethod() {
    this._paymentService.getDefaultCard()
      // .pipe(finalize(() => this._loaderService.stopLoader(this.PAYMENT_LOADER_ID)))
      .subscribe((data: any) => {
        this.censoredCardData = `${data.cardBrand} **** **** **** ${data.lastFour}`;
      },
        (error: any) => {
          if (error.status !== 404) {
            this._notificationService.error(error.error.message, 'Credit Card');
          }
        }
      );
  }

  loadGeneralForm() {
    if (this.employee) {
      if (this.employee.dateOfBirth) {
        this.gfc.dateOfBirth.patchValue(formatDate(this.employee.dateOfBirth, 'yyyy-MM-dd', 'en'));
      }
      this.gfc.gender.patchValue(this.employee.gender);
    }
  }

  loadContactInfoForm() {
    if (this.employee) {
      this.cfc.emailAddress.patchValue(this.employee.contactInfo.emailAddress);
      this.cfc.mobilePhone.patchValue(this.employee.contactInfo.mobilePhone);
      this.cfc.homePhone.patchValue(this.employee.contactInfo.homePhone);
      this.cfc.isOptedIn.patchValue(this.employee.contactInfo.isOptedIn);
      if (!this.employee.contactInfo.isOptedIn) {
        this.isShowOptIn = true;
      }
    }
  }

  loadLoginForm() {
    this.lfc.username.patchValue(this._authService.getUsername());
  }

  onLoginFormSubmit() {
    this.loginSubmitted = true;
    if (this.loginForm.invalid) {
      return;
    }

    this._loaderService.start();

    let password = this.loginForm.controls.newPassword.value;
    if (this.employee) {
      this._loginService.updatePassword(password)
        .pipe(
          finalize(() =>
            this._loaderService.stop()))
        .subscribe(() => {
          this._notificationService.success(this.N_SUCCESS_PASSWORD_MSG, this.N_SUCCESS_PASSWORD_TITLE)
        }, (error: any) => {
          if (error.error.message) {
            this._notificationService.error(error.error.message, this.N_SUCCESS_PASSWORD_TITLE);
          }
        });
    }
  }

  onContactFormSubmit() {
    this.contactSubmitted = true;
    if (this.contactInfoForm.invalid) {
      return;
    }

    this._loaderService.start();

    let contactInfo: EmployeeContactInfoRequest = this.buildContactRequest()

    this._employeeContactInfoService.update(contactInfo)
      .pipe(
        finalize(() =>
          this._loaderService.stop()))
      .subscribe(() => {
        this._notificationService.success(this.N_CONTACT_SUCCESS_MSG, this.N_CONTACT_TITLE);
      }, (error: any) => {
        let err_msg = this.N_UPDATE_ERROR_MSG;
        if (error.error.message) {
          err_msg = error.error.message;
        }
        this._notificationService.error(err_msg, this.N_CONTACT_TITLE);
      });

  }

  private buildContactRequest(): EmployeeContactInfoRequest {
    let contact: EmployeeContactInfoRequest = {
      emailAddress: this.contactInfoForm.controls.emailAddress.value,
      homePhone: this.contactInfoForm.controls.homePhone.value,
      mobilePhone: this.contactInfoForm.controls.mobilePhone.value,
      isOptedIn: this.contactInfoForm.controls.isOptedIn.value,
    }
    if (this.employee?.contactInfo.isSubscribed) {
      contact.isSubscribed = this.employee?.contactInfo.isSubscribed;
    }

    return contact;
  }

  onAddressFormSubmit() {
    this.addressComponent.submit().subscribe({
      next: address => {
        if (!address) return;
        this._loaderService.start();
        this._employeeAddressService.update(address)
          .pipe(
            finalize(() => this._loaderService.stop())
          ).subscribe({
            next: () => this._notificationService.success(this.N_SUCCESS_ADDRESS_MSG, this.N_SUCCESS_ADDRESS_TITLE),
            error: (error: any) => console.log(error)
          });
      }
    });
  }

  onUpdateGeneralInfo() {
    if (this.employee) {
      let tempEmployee = { ...this.employee }; // Clone object
      tempEmployee.dateOfBirth = this.gfc.dateOfBirth.value;
      tempEmployee.gender = this.gfc.gender.value;
      this._employeeService.update(tempEmployee)
        .subscribe(
          () => {
            if (this.employee) {
              this.employee.gender = tempEmployee.gender;
              this.employee.dateOfBirth = tempEmployee.dateOfBirth;
            }
            this._notificationService.success(this.N_GENERAL_INFO_SUCCESS_MSG, this.N_GENERAL_INFO_TITLE);
          },
          (error) => {
            this.loadGeneralForm();
            let errMsg = this.N_UPDATE_ERROR_MSG;
            if (error.error.message) {
              errMsg = error.error.message;
            }
            this._notificationService.error(errMsg, this.N_GENERAL_INFO_TITLE);
          }
        )
    }
  }
}
